import { Box, BoxProps, ChakraProps, colors, Flex } from "@biblioteksentralen/react";
import {
  digitalLibraryServiceCategories,
  ResolvedDigitalLibraryService,
  ResolvedEventSummary,
  ResolvedServiceSummary,
  targetAudiences,
} from "@libry-content/common";
import { useTranslation } from "../utils/hooks/useTranslation";
import { useCommonData } from "./layout/CommonDataProvider";
import { useSitePalette } from "../utils/useSitePalette";
import { ReactNode } from "react";

type ServiceBadgesProps = {
  service: ResolvedServiceSummary;
  hideCategory?: boolean;
};

export const ServiceBadges = ({ service, hideCategory, ...chakraProps }: ServiceBadgesProps & ChakraProps) => {
  const { ts } = useTranslation();
  const { site } = useCommonData();

  const moreThanOneLibraryTotal = site?.libraryOrdering && site.libraryOrdering.length > 1;

  const allLibrariesHasService = site?.libraryOrdering?.every(({ _ref }) =>
    service?.libraries?.some((library) => library._id === _ref)
  );

  const libraries = allLibrariesHasService
    ? ["Alle bibliotek"]
    : service?.libraries?.map((library) => ts(library.name));

  const targetAudienceLabels = service?.targetAudiences?.map((targetAudienceCode) =>
    ts(targetAudiences[targetAudienceCode])
  );

  const serviceTypeLabel = ts(service?.serviceType?.label);

  return (
    <BadgesContainer {...chakraProps}>
      {moreThanOneLibraryTotal &&
        libraries?.map((library) => (
          <Badge key={library} background={colors.grey80} color="white">
            {library}
          </Badge>
        ))}
      {!hideCategory && <Badge background={colors.grey30}>{serviceTypeLabel}</Badge>}
      {targetAudienceLabels?.map((label) => (
        <Badge key={label} background={colors.grey15}>
          {label}
        </Badge>
      ))}
    </BadgesContainer>
  );
};

export const EventBadges = ({ event, ...chakraProps }: { event: ResolvedEventSummary } & ChakraProps) => {
  const { ts } = useTranslation();
  const palette = useSitePalette();

  return (
    <BadgesContainer {...chakraProps}>
      <Badge {...palette.colors.lightaccent1.css}>{ts(event.eventType?.label)}</Badge>
      {event.targetAudiences?.map((code) => (
        <Badge {...palette.colors.lightaccent2.css} key={code}>
          {ts(targetAudiences[code])}
        </Badge>
      ))}
    </BadgesContainer>
  );
};

export const DigitalLibraryServiceBadges = ({
  digitalLibraryService,
  ...chakraProps
}: { digitalLibraryService: ResolvedDigitalLibraryService } & ChakraProps) => {
  const { ts } = useTranslation();
  const palette = useSitePalette();
  if (!digitalLibraryService.category) return null;

  return (
    <BadgesContainer {...chakraProps}>
      <Badge {...palette.colors.lightaccent1.css}>
        {ts(digitalLibraryServiceCategories[digitalLibraryService.category])}
      </Badge>
    </BadgesContainer>
  );
};

const BadgesContainer = ({ children, ...chakraProps }: { children: ReactNode } & ChakraProps) => (
  <Flex gap=".2rem" flexWrap="wrap" fontSize="xs" {...chakraProps}>
    {children}
  </Flex>
);

const Badge = (props: { children: React.ReactNode } & BoxProps) => (
  <Box fontWeight={600} padding=".2em .5em .1em" borderRadius=".25em" textTransform="uppercase" {...props} />
);
